import React, { Fragment } from "react";
import get from "lodash/get";
import { STATE_KEYS } from "utils/constants/state";
import { checkoutConfigToSummaryList } from "utils/checkout";
import startCase from "lodash/startCase";
import SlimCTA from "components/SlimCTA";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchAttachments } from "actions/attachment";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { fetchAppCheckout } from "actions/app";
import { ensureResources } from "utils/hooks";
import StatusIcon from "components/StatusIcon";
import Pill from "components/Pill";
import { STATUS, UI_THEME } from "utils/constants/ui";
import { getDiscountModels } from "utils/discount";
import { prepareCouponShow } from "utils/coupon";
import { prepareTaxRateShow } from "utils/taxRate";
import { prepareShippingRateShow } from "utils/shippingRate";
import { PAYMENT_METHOD_LABEL_MAP } from "utils/constants/payment";
import { useFormikContext } from "formik";
import { PAYMENT_METHOD_TYPE_ICON_MAP } from "components/PaymentMethodIcons";
import VideoModal from "components/VideoModal";
import { COMMAND_CONTENT_ID } from "components/CommandPalette/constants";

const SUMMARY_KEY = {
  DISCOUNTS: "discounts",
  REDIRECTS: "redirects",
  ATTACHMENTS: "attachments",
  TAX: "tax",
  PAYMENT: "payment",
  SHIPPING: "shipping",
  INSTALLMENTS: "installments",
  TRIAL: "trial"
};

const CommonCheckoutSummary = ({ name, showMenu, actions }) => {
  const { values } = useFormikContext();
  const checkoutCommonPath = `${name}.${STATE_KEYS.FORM.CONFIG_CHECKOUT_COMMON}`;
  const checkoutCommon = get(values, checkoutCommonPath, {});
  const { coupons, promotionCodes, taxRates, shippingRates } = ensureResources(
    actions
  );
  const admin = get(values, STATE_KEYS.MANIFEST.ADMIN);

  /**
   * Cancel url is locked to be the same as the live page location
   */
  const summary = checkoutConfigToSummaryList({
    ...checkoutCommon,
    cancel_url: admin && admin.liveUrl ? admin.liveUrl.split("?")[0] : ""
  });

  let nodes = [];
  for (const key in summary) {
    const value = summary[key];
    let nodeContent;

    if (key === SUMMARY_KEY.DISCOUNTS) {
      const discountModels = getDiscountModels({
        discounts: value.models,
        coupons,
        promotionCodes
      });

      nodeContent = (
        <Fragment>
          {value.allow_promotion_codes && (
            <div className="flex flex-row justify-between items-center pt3">
              <div className="f6 fw5">Allow promotion codes</div>
              <StatusIcon status={STATUS.SUCCESS} size={20} />
            </div>
          )}
          {discountModels.coupons && discountModels.coupons.length ? (
            <ul className="ma0 pa0 list w-100">
              {discountModels.coupons.map((coupon, cix) => {
                const preparedCoupon = prepareCouponShow({ coupon });
                return (
                  <div
                    key={cix}
                    className="flex flex-row justify-between items-center pt3"
                  >
                    <div className="f6 fw5">{coupon.name}</div>
                    <div className="f6">{preparedCoupon.discount}</div>
                  </div>
                );
              })}
            </ul>
          ) : null}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.REDIRECTS) {
      nodeContent = (
        <Fragment>
          {value.success_url && (
            <div className="flex flex-column justify-between items-start pt3">
              <div className="f6 fw5">Success url</div>
              <div className="f6 pt2 word-break">{value.success_url}</div>
            </div>
          )}
          {value.cancel_url && (
            <div className="flex flex-column justify-between items-start pt3">
              <div className="f6 fw5">Cancel url</div>
              <div className="f6 pt2 word-break">{value.cancel_url}</div>
            </div>
          )}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.TRIAL) {
      nodeContent = (
        <Fragment>
          {value.days && (
            <div className="flex flex-row justify-between items-center pt3">
              <div className="f6 fw5">Days</div>
              <div className="f6 pt2 word-break">{value.days}</div>
            </div>
          )}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.INSTALLMENTS) {
      nodeContent = (
        <Fragment>
          {value.iterations && (
            <div className="flex flex-column justify-between items-start pt3">
              <div className="f6 fw5">Payments</div>
              <div className="f6 pt2 word-break">{value.iterations}</div>
            </div>
          )}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.ATTACHMENTS) {
      const attachmentModels = attachments.filter(({ uuid }) =>
        value.indexOf(uuid)
      );
      nodeContent = attachmentModels.map((attachment) => {
        const preparedAttachment = prepareAttachmentShow({ attachment });
        return (
          <div
            className="flex flex-row justify-between items-start pt3"
            key={attachment.id}
          >
            <div className="f6 fw5">{preparedAttachment.name}</div>
            <div className="f6 flex flex-shrink-0">
              {preparedAttachment.size}
            </div>
          </div>
        );
      });
    } else if (key === SUMMARY_KEY.TAX) {
      const taxRateModels =
        value.rates &&
        value.rates.length &&
        taxRates.filter(({ id }) => value.rates.indexOf(id) > -1);

      nodeContent = (
        <Fragment>
          {value.automatic && (
            <div className="flex flex-row justify-between items-center pt3">
              <div className="f6 fw5">Automatic taxes</div>
              <StatusIcon status={STATUS.SUCCESS} size={20} />
            </div>
          )}
          {value.id_collection_type && (
            <div className="flex flex-row justify-between items-center pt3">
              <div className="f6 fw5">Capture ID</div>
              <div className="f6 word-break">
                {startCase(value.id_collection_type)}
              </div>
            </div>
          )}
          {taxRateModels && taxRateModels.length ? (
            <ul className="ma0 pa0 list w-100">
              {taxRateModels.map((taxRate, cix) => {
                const preparedRate = prepareTaxRateShow({ taxRate });

                return (
                  <div
                    key={cix}
                    className="flex flex-row justify-between items-center pt3"
                  >
                    <div className="flex flex-row flex-shrink-0">
                      <div className="f6">{`${preparedRate.displayName} - ${preparedRate.percentage}`}</div>
                    </div>
                    <div className="dib">
                      <Pill theme={UI_THEME.SLIM} copy={preparedRate.type} />
                    </div>
                  </div>
                );
              })}
            </ul>
          ) : null}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.PAYMENT) {
      const authorize = value.authorize ? (
        <div className="flex flex-row justify-between items-center pt3">
          <div className="f6 fw5">Authorize</div>
          <StatusIcon status={STATUS.SUCCESS} size={20} />
        </div>
      ) : null;

      const savePaymentMethod = value.setup_future_usage ? (
        <div className="flex flex-row justify-between items-center pt3">
          <div className="f6 fw5">Save payment method</div>
          <div className="f6 word-break">
            {startCase(value.setup_future_usage)}
          </div>
        </div>
      ) : null;

      const methods = value.methods && value.methods.length > 0 && (
        <div className="flex flex-column justify-between items-start pt3">
          <div className="f6 fw5">Methods</div>
          <ul className="f6 pt2 word-break list ma0 pa0">
            {value.methods.map((method, methodIx) => {
              const TypeIcon = PAYMENT_METHOD_TYPE_ICON_MAP[method];
              return (
                <li key={method} className={methodIx ? "pt1" : "pt0"}>
                  <div className="flex flex-row items-center w-100">
                    <div
                      className="flex items-center justify-center"
                      style={{ width: 32, height: 32 }}
                    >
                      {TypeIcon ? <TypeIcon /> : null}
                    </div>
                    <div className="flex flex-row items-center justify-between w-100">
                      <div className="f6 pl2 w-100">
                        {PAYMENT_METHOD_LABEL_MAP[method]}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      );

      const mode = value.mode ? (
        <div className="flex flex-row justify-between items-center pt3">
          <div className="f6 fw5">Mode</div>
          <div className="f6 word-break">{startCase(value.mode)}</div>
        </div>
      ) : null;

      nodeContent = (
        <Fragment>
          {authorize}
          {savePaymentMethod}
          {methods}
          {mode}
        </Fragment>
      );
    } else if (key === SUMMARY_KEY.SHIPPING) {
      const shippingModels =
        value.options &&
        value.options.length &&
        shippingRates.filter(({ id }) =>
          value.options.find(({ shipping_rate }) => shipping_rate === id)
        );

      let destinationContent;
      if (value.worldwide) {
        destinationContent = (
          <div className="flex flex-row justify-between items-center pt3">
            <div className="f6 fw5">Worldwide</div>
            <StatusIcon status={STATUS.SUCCESS} size={20} />
          </div>
        );
      } else if (value.countries && value.countries.length) {
        destinationContent = (
          <div className="flex flex-column justify-between items-start pt3">
            <div className="f6 fw5">Countries</div>
            <div className="f6 pt2 word-break">
              {value.countries.join(", ")}
            </div>
          </div>
        );
      }

      nodeContent = (
        <Fragment>
          {destinationContent}
          {shippingModels && shippingModels.length ? (
            <ul className="ma0 pa0 list w-100">
              {shippingModels.map((shippingRate, cix) => {
                const preparedRate = prepareShippingRateShow({ shippingRate });

                return (
                  <div
                    key={shippingRate.id}
                    className="flex flex-row justify-between items-center pt3"
                  >
                    <div className="f6 fw5">{preparedRate.displayName}</div>
                    <div className="f6">{preparedRate.formattedPrice}</div>
                  </div>
                );
              })}
            </ul>
          ) : null}
        </Fragment>
      );
    }

    nodes.push(
      <div className="w-100 flex flex-column" key={key}>
        <div className="fw6">{startCase(key)}</div>
        {nodeContent}
      </div>
    );
  }

  let content;
  if (nodes.length) {
    content = (
      <div className="w-100 pa3">
        <div
          className="f6 fw6 link color-primary link__decoration--hover pb3 pointer tr mb3 pb3 hairline-1 bb"
          onClick={showMenu}
        >
          Edit
        </div>

        <ul className="list ma0 pa0 flex flex-column">
          {nodes.map((node, nodeIx) => (
            <div
              key={nodeIx}
              className={`w-100 bb hairline-1 pb3 mb2 ${nodeIx ? "pt2" : ""}`}
            >
              {node}
            </div>
          ))}
        </ul>
      </div>
    );
  } else {
    content = (
      <div className="pa3">
        <SlimCTA
          subheader="Add common checkout settings for discounts, payment types and more."
          customClasses={{
            container:
              "w-100 flex flex-column justify-between ba br2 hairline-1 pa3 items-start bg-white",
            actionsWrapper: "pt2"
          }}
          actions={[
            {
              copy: "Add",
              onClick: showMenu
            }
          ]}
          extraActions={[
            <div
              key={COMMAND_CONTENT_ID.CHECKOUT.COMMON_CONFIG}
              className="ml2 flex"
            >
              <VideoModal
                copy="Learn"
                id={COMMAND_CONTENT_ID.CHECKOUT.COMMON_CONFIG}
              />
            </div>
          ]}
        ></SlimCTA>
      </div>
    );
  }

  return content;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(CommonCheckoutSummary);
